import { useTeams } from "@/contexts/teams-provider";
import { useApiStream } from "@/hooks/useApi";
import {
  companyWebPreviewKey,
  companyWebPreviewStore,
  type CompanyWebPreviewState,
} from "@/stores/company-web-preview-store";
import type { CompanyTag } from "@shaarp/common";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";

const LOADING: CompanyWebPreviewState = { phase: "loading" };

/**
 * The web synthesis of a tagged company, from the module-level store: it starts
 * the request on mount when none ran for this company, and re-attaches to the
 * running or finished one otherwise. The tag is a new object on every render of
 * a list, so only its identity key decides when to start. `retry` asks again
 * after a failure.
 *
 * The key is `companyWebPreviewKey`: the selected team, then an identity never
 * coarser than the one the server stores the profile under — two tags sharing
 * an entry here always get the same profile there. Switching teams starts the
 * request again for the new one. `attachCompany` records the company created
 * from the panel under that same key.
 */
export function useCompanyWebPreview(tag: CompanyTag): {
  state: CompanyWebPreviewState;
  retry: () => void;
  attachCompany: (companyId: string) => void;
} {
  const stream = useApiStream();
  const { teamSelected } = useTeams();
  const key = companyWebPreviewKey(teamSelected?.id, tag);

  // The latest tag and `post`, read by the effect without re-running it.
  const latest = useRef({ tag, post: stream.post });
  latest.current = { tag, post: stream.post };

  const state = useSyncExternalStore(companyWebPreviewStore.subscribe, () =>
    companyWebPreviewStore.getState(key),
  );

  // Also when the entry disappears under a mounted panel — the store evicted
  // it to make room — so the panel starts it again instead of waiting forever.
  const isMissing = state === undefined;
  useEffect(() => {
    void companyWebPreviewStore.start(
      key,
      latest.current.tag,
      latest.current.post,
    );
  }, [key, isMissing]);

  const retry = useCallback(() => {
    void companyWebPreviewStore.retry(
      key,
      latest.current.tag,
      latest.current.post,
    );
  }, [key]);

  const attachCompany = useCallback(
    (companyId: string) => companyWebPreviewStore.attachCompany(key, companyId),
    [key],
  );

  return { state: state ?? LOADING, retry, attachCompany };
}
